import type { GameType, MatchConfig, MatchState } from "../types.js";
import { isTeamFormat, throwOrder } from "./teamPlay.js";

export const HIGHSCORE_ROUNDS = 7;

export function usesThrowOrderPopup(gameType: GameType): boolean {
  return (
    gameType === "cricket" ||
    gameType === "elimination" ||
    gameType === "clock" ||
    gameType === "shanghai" ||
    gameType === "highscore"
  );
}

export function defaultThrowSequence(n: number): number[] {
  const count = Math.max(1, n);
  return Array.from({ length: count }, (_, i) => i);
}

/**
 * Scoreboard slot order: first thrower at the top (or leftmost on the cricket board).
 * Follows the sequence set after the previous match; roster order for x01.
 */
export function displayPlayerOrder(config: MatchConfig): number[] {
  const n = Math.max(1, config.players?.length ?? 1);
  if (isTeamFormat(config) || !usesThrowOrderPopup(config.gameType)) {
    return Array.from({ length: n }, (_, i) => i);
  }
  return throwOrder(config);
}

/** x01: 2 players → Spieler 2 starts; otherwise Spieler 1. Popup games always start with the first slot. */
export function soloFirstThrowerForCount(gameType: GameType, count: number): number {
  if (usesThrowOrderPopup(gameType)) return 0;
  return count === 2 ? 1 : 0;
}

export function sanitizeThrowSequence(seq: number[] | undefined, n: number): number[] {
  const count = Math.max(1, n);
  const seen = new Set<number>();
  const out: number[] = [];
  for (const raw of seq ?? []) {
    const i = Math.round(raw);
    if (!Number.isInteger(i) || i < 0 || i >= count || seen.has(i)) continue;
    seen.add(i);
    out.push(i);
  }
  for (let i = 0; i < count; i++) {
    if (!seen.has(i)) out.push(i);
  }
  return out;
}

/** Rank indices low-to-high by `scoreOf` (lower = earlier in throw order). Ties keep roster order. */
function rankByScore(n: number, scoreOf: (index: number) => number, winnerLast: boolean, winnerIndex: number | null): number[] {
  const indices = Array.from({ length: n }, (_, i) => i);
  indices.sort((a, b) => {
    if (winnerLast && winnerIndex != null) {
      if (a === winnerIndex) return 1;
      if (b === winnerIndex) return -1;
    }
    const diff = scoreOf(a) - scoreOf(b);
    if (diff) return diff;
    return a - b;
  });
  return indices;
}

/**
 * Default throw order after a finished match.
 * Cricket / Clock / Shanghai / Highscore: loser first, winner last.
 * Elimination: winner first, then next-highest score.
 */
export function rankingThrowSequence(state: MatchState): number[] {
  const n = state.config.players.length;
  const winner = state.lastWinnerIndex;
  const legs = state.currentLeg.players;
  const game = state.config.gameType;

  if (game === "elimination") {
    return rankByScore(n, (i) => -(legs[i]?.remaining ?? 0), false, null);
  }
  if (game === "shanghai" || game === "highscore") {
    const score = (i: number) =>
      game === "highscore" ? (legs[i]?.highscoreScore ?? 0) : (legs[i]?.shanghaiScore ?? 0);
    return rankByScore(n, (i) => score(i), true, winner);
  }
  if (game === "clock") {
    return rankByScore(
      n,
      (i) => {
        const p = legs[i];
        if (!p) return 0;
        if (p.clockFinished) return 100;
        return p.nextTarget ?? 0;
      },
      true,
      winner,
    );
  }
  if (game === "cricket") {
    return rankByScore(n, (i) => legs[i]?.cricketScore ?? 0, true, winner);
  }
  return defaultThrowSequence(n);
}

export function applyThrowSequence(config: MatchConfig, sequence: number[] | undefined): MatchConfig {
  const seq = sanitizeThrowSequence(sequence, config.players.length);
  return {
    ...config,
    throwSequence: seq,
    firstThrowerIndex: isTeamFormat(config) ? config.firstThrowerIndex : (seq[0] ?? 0),
  };
}
